<script>
  import { spendReview, finishSpendReview } from '../state/spend-review.svelte.js';
  import { focusDialog } from '../utils/dialog-focus.js';

  function amount(value) {
    const numeric = Number(value);
    return numeric > 0 && numeric < 0.001 ? '<0.001' : numeric.toLocaleString(undefined, { maximumFractionDigits: 3 });
  }
</script>

{#if spendReview.open && spendReview.quote}
  <div class="charge-backdrop">
    <div class="charge-dialog" role="dialog" aria-modal="true" aria-labelledby="charge-title" tabindex="-1"
      use:focusDialog={{ initialFocus: 'dialog', onClose: () => finishSpendReview(false) }}>
      <h2 id="charge-title">{spendReview.title || 'Review your generation'}</h2>
      <p class="description">This request will use credits. Review the complete selection before continuing.</p>
      <div class="quote-lines">
        {#each spendReview.quote.lines as line}
          <div class="quote-line">
            <div>
              <strong>{line.modelName}</strong>
              <span>{line.count} {spendReview.quote.kind === 'prompt' ? 'prompt request(s)' : spendReview.quote.kind === 'video' ? 'video' : 'images'}</span>
              {#if line.settings?.width}<span>{line.settings.outputWidth || line.settings.width} × {line.settings.outputHeight || line.settings.height}{line.settings.gptImageQuality ? ` · ${line.settings.gptImageQuality} quality` : ''}</span>{/if}
              {#if spendReview.quote.kind === 'video' && line.settings?.outputWidth !== line.settings?.width}<span>{line.settings.width} × {line.settings.height} render canvas · two-stage output</span>{/if}
              {#if line.settings?.stepCount}<span>{line.settings.stepCount} steps · {line.settings.network || 'fast'}</span>{/if}
              {#if line.settings?.contextImages}<span>{line.settings.contextImages} original reference image(s)</span>{/if}
              {#if line.settings?.durationSeconds}<span>{Number(line.settings.durationSeconds).toFixed(2)}s · {line.settings.fps} fps</span>{/if}
              {#if spendReview.quote.kind === 'video'}
                <span>{line.settings?.steps ? `${line.settings.steps} steps · ` : ''}{line.settings?.network} · {line.settings?.generateAudio ? 'audio' : 'silent'}</span>
                {#if line.settings?.seed !== undefined}<span>Seed: {line.settings.seed}</span>{/if}
              {/if}
            </div>
            <div class="line-price">
              {#if line.covered}<span class="covered">Included in your plan</span>
              {:else}<strong>{amount(line.amount)} Spark</strong>{#if line.premium}<span>Requires Premium Spark access</span>{/if}{/if}
            </div>
          </div>
        {/each}
      </div>
      <div class="total"><span>Total credit estimate</span><strong>{amount(spendReview.quote.total)} Spark</strong></div>
      {#if Number(spendReview.quote.premiumTotal) > 0}
        <p class="note">{amount(spendReview.quote.premiumTotal)} Spark of this estimate is for models requiring Premium Spark access. Charges use your total Spark balance. Partner models are billed separately from Unlimited and Unlimited Pro.</p>
      {/if}
      {#if spendReview.discountBps > 0 && spendReview.quote.lines.some(line => line.partner && line.discountBpsApplied === spendReview.discountBps)}
        <p class="note">Your {spendReview.discountBps / 100}% member partner-model discount is included in this estimate.</p>
      {/if}
      {#if spendReview.quote.lines.some(line => line.partner)}
        <p class="note">{spendReview.quote.kind === 'images' ? 'Reference-edit prompt costs are included. ' : ''}This is an estimate; final partner-model credit use can vary.</p>
      {/if}
      {#if spendReview.balance}
        <p class="balance">Available: {amount(spendReview.balance.spark)} Spark. Premium Spark access: {Number(spendReview.balance.premiumSpark) > 0 ? 'available' : 'not available'}.</p>
      {/if}
      {#if spendReview.quote.kind === 'prompt'}<p class="note">Estimate for up to three structured-output attempts. Only attempts that run are billed; actual token use can vary.</p>{/if}
      <p class="note">The prompts, models, seeds, settings, and references in this request stay fixed. An account or settings change requires another review.</p>
      <div class="actions">
        <button class="cancel" onclick={() => finishSpendReview(false)}>Cancel</button>
        <button class="confirm" onclick={() => finishSpendReview(true)}>Confirm credit billing</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .charge-backdrop { position: fixed; inset: 0; z-index: 1100; background: #000a; display: flex; align-items: center; justify-content: center; padding: 16px; }
  .charge-dialog { width: min(560px, 100%); max-height: 90dvh; overflow-y: auto; background: var(--color-bg, #fff); color: var(--color-text, #1a1a1a); border: 1px solid var(--color-border, #e0e0e0); border-radius: 20px; padding: 24px; }
  h2 { margin: 0 0 12px; font-size: 1.4rem; }
  .description, .note, .balance { color: var(--color-text-muted, #666); font-size: 0.9rem; line-height: 1.5; }
  .quote-lines { margin: 20px 0; }
  .quote-line { display: flex; gap: 16px; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid var(--color-border, #e0e0e0); }
  .quote-line span { display: block; font-size: 0.8rem; margin-top: 4px; }
  .line-price { text-align: right; min-width: 110px; font-size: 0.9rem; }
  .covered { color: #047857; }
  .total { display: flex; justify-content: space-between; gap: 16px; font-size: 1.1rem; }
  .actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 22px; position: sticky; bottom: 0; padding-top: 12px; background: var(--color-bg, #fff); }
  button { min-height: 44px; border-radius: 12px; padding: 10px 16px; font-weight: 600; cursor: pointer; color: var(--color-text, #1a1a1a); }
  .cancel { border: 1px solid var(--color-border, #e0e0e0); background: transparent; }
  .confirm { border: 0; color: #fff; background: var(--color-primary, #FF1B8D); }
  button:focus-visible { outline: 2px solid var(--color-primary, #FF1B8D); outline-offset: 3px; }
  @media (max-width: 480px) { .charge-dialog { padding: 18px; } .quote-line { flex-direction: column; gap: 4px; } .line-price { text-align: left; } .actions { flex-wrap: wrap; } .actions button { flex: 1; } }
</style>
